import { Vector2 } from "../../common/vector2.js";
import { BackButtonHandler } from "../../navigation/back_button_handler.js";
import { BasicEventsController } from "../../touch_events_controller/basic_events_controller.js";
import { AbstractOverlay } from "./abstract_overlay.js";
import { OverlayId } from "./overlay_ids.js";
import { audioController } from "../../audio/audio_controller.js";
import { AudioClips } from "../../audio/audio_constants.js";
import { CrashBreadcrumbs } from "../../crash/breadcrumbs.js";
import { collectDomImageUrls, getDomHost, resyncDomHost, showDomHostForRaster } from "../dom_backend/dom_backend.js";
import { ImagesHolder } from "../../image_loader/images_holder.js";
import { isNullCanvasContext } from "../dom_backend/null_canvas_context.js";
import {
  appearKeyframes,
  closeKeyframes,
  fadeBackdrop,
  primeNodeForRaster,
  primeScaleFor,
  runSurfaceAnimation,
  snapBackdrop,
  surfaceAnimationsSupported,
  unprimeNode,
  waitForPresentedFrame,
} from "../dom_backend/surface_motion.js";

export class OverlayManager
{
  public static ctx: CanvasRenderingContext2D;
  public static gl: WebGL2RenderingContext;

  public static readonly BACKDROP_FADE_MS = 160;

  private static overlay_stack: AbstractOverlay[] = [];
  private static closing_overlays = new Set<AbstractOverlay>();
  private static canvas: HTMLCanvasElement;
  private static events_Controller: BasicEventsController;
  private static width: number;
  private static height: number;
  private static overlay_element: HTMLElement;
  private static interaction_blocked_until_ms = 0;
  private static backdrop_visible = false;
  private static hide_timer = 0;

  private static is_down_outside_of_fverlay: boolean = false;
  private static dom_host: HTMLDivElement | null = null;

  public static init(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number
  ): void
  {
    this.ctx = ctx;
    this.canvas = ctx.canvas;
    this.width = width;
    this.height = height;
    this.dom_host = isNullCanvasContext( ctx ) ? getDomHost( this.canvas ) : null;
    this.overlay_element = document.getElementById( 'overlayBackground' ) as HTMLElement;

    this.events_Controller = new BasicEventsController( this.canvas );
    this.events_Controller.init(
      ( p: Vector2 ) => this.handleDown( p ),
      ( p: Vector2 ) => this.handleUp( p ),
      ( p: Vector2 ) => this.handleMove( p )
    );
  }

  public static setScreenSize( width: number, height: number ): void
  {
    // Pooled overlays were laid out for the old size.
    if ( width !== this.width || height !== this.height )
      this.releasePool( "screen size changed" );

    this.width = width;
    this.height = height;

    // Called after the overlay canvas buffer was reallocated (blank): render()
    // only repaints dirty elements, so the open overlay must be fully redrawn.
    if ( this.overlay_stack.length > 0 )
      this.topOverlay().invalidateSubtree();
  }

  public static update( now: number ): void
  {
    if ( this.overlay_stack.length === 0 )
      return;

    const top_overlay = this.topOverlay();
    top_overlay.update( now );
  }

  public static render(): void
  {
    if ( this.overlay_stack.length === 0 )
      return;

    const top_overlay = this.topOverlay();
    if ( !top_overlay.shouldBeRendered() )
      return;

    if ( this.dom_host )
    {
      for ( const overlay of this.overlay_stack )
      {
        if ( !overlay.isDomMode() )
          continue;
        overlay.mountDomHost( this.canvas );
        const display = overlay === top_overlay ? "" : "none";
        const node = overlay.getDomNode();
        if ( node.style.display !== display )
          node.style.display = display;
      }
      top_overlay.render();
      return;
    }

    this.ctx.clearRect( 0, 0, this.width, this.height );
    top_overlay.render();
  }

  public static isDomMode(): boolean
  {
    return this.dom_host !== null;
  }

  private static handleDown( p: Vector2 ): void
  {
    if ( this.overlay_stack.length === 0 )
      return;

    if ( this.isInsideOpenedOverlay( p ) )
    {
      const topOverlay = this.topOverlay();
      topOverlay.onTouchStart( p, performance.now() );
      return;
    }

    this.is_down_outside_of_fverlay = true;
  }

  private static handleUp( p: Vector2 ): void
  {
    if ( this.overlay_stack.length === 0 )
      return;

    const top_overlay = this.topOverlay();

    if ( this.isInsideOpenedOverlay( p ) )
    {
      top_overlay.onTouchEnd( p, performance.now() );

      if ( top_overlay.getCloseOnBackgroundClick() )
      {
        this.closeOverlay();
      }
      return;
    }

    if ( !this.is_down_outside_of_fverlay )
      return;

    this.is_down_outside_of_fverlay = false;
    if ( top_overlay.getCloseOnBackgroundClick() )
    {
      this.closeOverlay();
    }
  }

  private static handleMove( p: Vector2 ): void
  {
    if ( this.overlay_stack.length === 0 )
      return;

    if ( this.isInsideOpenedOverlay( p ) )
    {
      const top_overlay = this.topOverlay();
      top_overlay.onTouchMove( p, performance.now() );
      return;
    }
  }

  private static isInsideOpenedOverlay( p: Vector2 ): boolean
  {
    if ( this.overlay_stack.length === 0 )
      return false;

    const top_overlay = this.topOverlay();
    return top_overlay.containsPointGlobal( p );
  }

  public static openOverlay( overlay: AbstractOverlay ): void
  {
    if ( this.prewarming.length > 0 )
      this.finishPrewarm();
    void audioController.playSound( AudioClips.UI_CLICK );
    CrashBreadcrumbs.add( 'dialog', `overlay open ${overlay.name?.() ?? 'unknown'}` );
    this.overlay_stack.push( overlay );
    if ( this.dom_host && overlay.isDomMode() )
      overlay.mountDomHost( this.canvas );
    this.ctx.clearRect( 0, 0, this.width, this.height );
    this.show();
    if ( this.useDomAnimations( overlay ) )
      this.animateAppearDom( overlay );
    BackButtonHandler.getInstance().updateBackButtonVisibility();
  }

  public static closeOverlay(): void
  {
    if ( !this.isAnyOverlayOpened() )
      return;

    void audioController.playSound( AudioClips.UI_CLICK );
    // Consume the pointer release that closed the overlay so the same event
    // cannot click UI on the screen underneath it.
    this.interaction_blocked_until_ms = performance.now() + 100;

    const closed = this.overlay_stack.pop() as AbstractOverlay;
    CrashBreadcrumbs.add( 'dialog', `overlay close ${closed.name?.() ?? 'unknown'}` );
    this.callWillClose( closed );

    const close_motion = this.useDomAnimations( closed ) ? closed.getCloseMotion() : null;
    let linger_ms = 0;
    if ( close_motion )
    {
      linger_ms = close_motion.duration_ms;
      this.animateCloseDom( closed, close_motion.to_scale, close_motion.duration_ms );
    }
    else
    {
      this.callCleanup( closed );
    }

    if ( !this.isAnyOverlayOpened() )
    {
      this.ctx.clearRect( 0, 0, this.width, this.height );
      this.hide( linger_ms );
    }
    else
    {
      this.ctx.clearRect( 0, 0, this.width, this.height );
      this.updateViewportBackground();
    }
    BackButtonHandler.getInstance().updateBackButtonVisibility();
  }

  private static canvas_anim: Animation | null = null;
  private static readonly EASE_OUT_QUAD = "cubic-bezier(0.25, 0.46, 0.45, 0.94)";

  public static useCssAnimations(): boolean
  {
    return !this.dom_host
      && typeof this.canvas?.animate === "function"
      && localStorage.getItem( "perf_diag_dialog_canvas_anim" ) !== "1";
  }

  public static useDomAnimations( overlay: AbstractOverlay ): boolean
  {
    return this.dom_host !== null
      && overlay.isDomMode()
      && surfaceAnimationsSupported( overlay.getDomNode() );
  }

  public static cancelCanvasAnim(): void
  {
    if ( !this.canvas_anim )
      return;

    this.canvas_anim.cancel();
    this.canvas_anim = null;
  }

  public static animateAppearCss(
    overlay: AbstractOverlay,
    from_scale: number,
    duration_ms: number
  ): void
  {
    this.cancelCanvasAnim();

    const center = overlay.getCenterGlobal();
    this.canvas.style.transformOrigin = `${center.x}px ${center.y}px`;

    const anim = this.canvas.animate( [
      { transform: `scale(${from_scale})`, opacity: 0, easing: this.EASE_OUT_QUAD },
      { transform: "scale(1)", opacity: 1 },
    ], { duration: duration_ms, fill: "both" } );
    this.canvas_anim = anim;

    anim.onfinish = () =>
    {
      if ( this.canvas_anim === anim )
      {
        anim.cancel();
        this.canvas_anim = null;
      }
    };
  }

  private static animateAppearDom( overlay: AbstractOverlay ): void
  {
    const motion = overlay.getAppearMotion();
    if ( !motion )
      return;

    const node = overlay.getDomNode();
    overlay.cancelSurfaceAnimation();
    primeNodeForRaster( node, primeScaleFor( motion ) );

    void waitForPresentedFrame().then( () =>
    {
      const still_open = this.overlay_stack.indexOf( overlay ) >= 0;
      if ( !still_open || this.closing_overlays.has( overlay ) )
      {
        if ( still_open )
          unprimeNode( node );
        return;
      }

      unprimeNode( node );
      const { keyframes, duration_ms } = appearKeyframes( motion );
      overlay.setSurfaceAnimation( runSurfaceAnimation( node, keyframes, duration_ms, true ) );
    } );
  }

  private static animateCloseDom( overlay: AbstractOverlay, to_scale: number, duration_ms: number ): void
  {
    const node = overlay.getDomNode();
    const keyframes = closeKeyframes( node, { to_scale, duration_ms } );
    overlay.cancelSurfaceAnimation();
    unprimeNode( node );
    this.closing_overlays.add( overlay );

    const anim = runSurfaceAnimation( node, keyframes, duration_ms, false );
    overlay.setSurfaceAnimation( anim );
    const finish = () =>
    {
      if ( !this.closing_overlays.delete( overlay ) )
        return;
      this.callCleanup( overlay );
    };
    if ( !anim )
    {
      finish();
      return;
    }
    anim.finished.then( finish, finish );
    window.setTimeout( finish, duration_ms + 48 );
  }

  private static callWillClose( closed: AbstractOverlay ): void
  {
    try { closed.onOverlayWillClose(); }
    catch ( e ) { console.warn( "OverlayManager: onOverlayWillClose failed", e ); }
  }

  private static callCleanup( closed: AbstractOverlay ): void
  {
    closed.cancelSurfaceAnimation();
    if ( !closed.isDomMode() )
      return;

    if ( !this.recycle( closed ) )
      this.destroyOverlay( closed );
  }

  private static destroyOverlay( overlay: AbstractOverlay ): void
  {
    try { overlay.cleanup(); }
    catch ( e ) { console.warn( "OverlayManager: cleanup failed", e ); }
  }

  // ---- Pool (DOM mode): see AbstractOverlay.isPoolable ----------------------

  private static pool = new Map<string, AbstractOverlay>();
  private static readonly POOL_LIMIT = 6;
  private static prewarming: AbstractOverlay[] = [];
  private static readonly PREWARM_RASTER_OPACITY = 0.002;
  private static readonly PREWARM_DECODE_TIMEOUT_MS = 1500;

  public static hasPooled( key: string ): boolean
  {
    return this.pool.has( key );
  }

  public static takePooled( key: string ): AbstractOverlay | null
  {
    const pooled = this.pool.get( key );
    if ( !pooled )
      return null;
    this.pool.delete( key );
    if ( this.overlay_stack.includes( pooled ) || this.closing_overlays.has( pooled ) || this.prewarming.includes( pooled ) )
      return null;
    return pooled;
  }

  public static releasePool( reason: string ): void
  {
    this.finishPrewarm();
    if ( this.pool.size === 0 )
      return;
    console.info( `[OverlayManager] releasing overlay pool (${this.pool.size}): ${reason}` );
    for ( const overlay of this.pool.values() )
      this.destroyOverlay( overlay );
    this.pool.clear();
  }

  public static pooledNames(): string[]
  {
    return [ ...this.pool.keys() ];
  }

  private static recycle( overlay: AbstractOverlay ): boolean
  {
    if ( !this.dom_host || !overlay.isDomMode() || !overlay.isPoolable() )
      return false;

    const key = overlay.poolKey();
    const previous = this.pool.get( key );
    if ( previous && previous !== overlay )
      this.destroyOverlay( previous );
    this.pool.delete( key );

    try
    {
      overlay.hideIntoPool();
    }
    catch ( e )
    {
      console.warn( `[OverlayManager] hideIntoPool failed for ${key}, destroying`, e );
      return false;
    }

    this.pool.set( key, overlay );
    while ( this.pool.size > this.POOL_LIMIT )
    {
      const oldest_key = this.pool.keys().next().value as string;
      const oldest = this.pool.get( oldest_key ) as AbstractOverlay;
      this.pool.delete( oldest_key );
      this.destroyOverlay( oldest );
    }
    return true;
  }

  /**
   * Builds the overlays' DOM now (call it while the loading screen is up),
   * then decodes their pictures and lets the compositor raster them for a few
   * frames at near-zero opacity, and puts them in the pool: the first real
   * open is a reuse. Skipped while another overlay is up (the raster pass
   * would show over it); an overlay opening meanwhile cuts it short.
   */
  public static async prewarm( overlays: AbstractOverlay[] ): Promise<void>
  {
    const warm = overlays.filter( overlay => this.dom_host && overlay.isDomMode() && overlay.isPoolable() && !this.pool.has( overlay.poolKey() ) );
    for ( const overlay of overlays )
    {
      if ( !warm.includes( overlay ) )
        this.destroyOverlay( overlay );
    }
    if ( warm.length === 0 )
      return;
    if ( this.prewarming.length > 0 || this.isAnyOverlayOpened() )
    {
      for ( const overlay of warm )
        this.destroyOverlay( overlay );
      return;
    }

    this.prewarming = warm;
    const alive = (): boolean => this.prewarming === warm && !this.isAnyOverlayOpened();
    try
    {
      const urls = new Set<string>();
      for ( const overlay of warm )
      {
        const node = overlay.getDomNode();
        node.style.display = "none";
        overlay.mountDomHost( this.canvas );
        overlay.render();
        collectDomImageUrls( node, urls );
      }

      await this.decodeImages( urls );
      if ( !alive() )
        return;

      for ( const overlay of warm )
        overlay.getDomNode().style.display = "";
      showDomHostForRaster( this.canvas, this.PREWARM_RASTER_OPACITY );
      for ( let i = 0; i < 3; i++ )
      {
        await waitForPresentedFrame();
        if ( !alive() )
          return;
        for ( const overlay of warm )
        {
          overlay.update( performance.now() );
          overlay.render();
        }
      }
    }
    catch ( e )
    {
      console.warn( "[OverlayManager] prewarm failed", e );
    }
    finally
    {
      if ( this.prewarming === warm )
        this.finishPrewarm();
    }
  }

  /** The overlays warmed so far go to the pool as they are. */
  private static finishPrewarm(): void
  {
    const overlays = this.prewarming;
    if ( overlays.length === 0 )
      return;
    this.prewarming = [];
    for ( const overlay of overlays )
    {
      overlay.cancelSurfaceAnimation();
      if ( !this.recycle( overlay ) )
        this.destroyOverlay( overlay );
    }
    resyncDomHost( this.canvas );
  }

  private static async decodeImages( urls: Set<string> ): Promise<void>
  {
    const pending: Promise<unknown>[] = [];
    for ( const url of urls )
    {
      let img = ImagesHolder.getDomImageByUrl( url );
      if ( !img )
      {
        img = new Image();
        img.src = url;
      }
      pending.push( img.decode().catch( () => null ) );
    }
    if ( pending.length === 0 )
      return;
    await Promise.race( [
      Promise.all( pending ),
      new Promise( resolve => setTimeout( resolve, this.PREWARM_DECODE_TIMEOUT_MS ) ),
    ] );
  }

  public static isAnyOverlayOpened(): boolean { return this.overlay_stack.length > 0; }

  public static getTopOverlay(): AbstractOverlay | null { return this.topOverlaySafe(); }

  public static isInteractionBlocked(): boolean
  {
    return this.isAnyOverlayOpened() || performance.now() < this.interaction_blocked_until_ms;
  }

  public static isOverlayOpen( overlay_id: OverlayId ): boolean
  {
    if ( !this.isAnyOverlayOpened() )
      return false;

    const top_overlay = this.topOverlaySafe();
    return top_overlay?.getId() === overlay_id;
  }

  private static topOverlaySafe(): AbstractOverlay | null
  {
    if ( this.overlay_stack.length === 0 )
      return null;

    return this.overlay_stack[this.overlay_stack.length - 1];
  }

  private static topOverlay(): AbstractOverlay
  {
    if ( this.overlay_stack.length === 0 )
      throw new Error( 'topOverlay(): No overlays are open' );

    return this.overlay_stack[this.overlay_stack.length - 1];
  }

  private static hide( linger_ms: number = 0 ): void
  {
    this.cancelCanvasAnim();
    this.backdrop_visible = false;
    if ( this.hide_timer )
    {
      clearTimeout( this.hide_timer );
      this.hide_timer = 0;
    }

    const finish = () =>
    {
      this.hide_timer = 0;
      if ( this.backdrop_visible )
        return;
      this.canvas.style.display = "none";
      if ( this.overlay_element )
      {
        snapBackdrop( this.overlay_element, 0 );
        this.overlay_element.style.display = "none";
        this.overlay_element.style.background = "";
      }
    };

    if ( linger_ms <= 0 || !this.overlay_element )
    {
      finish();
      return;
    }

    fadeBackdrop( this.overlay_element, 0, linger_ms );
    this.hide_timer = window.setTimeout( finish, linger_ms + 64 );
  }

  private static show(): void
  {
    const was_visible = this.backdrop_visible;
    this.backdrop_visible = true;
    if ( this.hide_timer )
    {
      clearTimeout( this.hide_timer );
      this.hide_timer = 0;
    }

    this.canvas.style.display = "block";
    if ( !this.overlay_element )
      return;

    this.updateViewportBackground();
    if ( was_visible )
    {
      snapBackdrop( this.overlay_element, 1 );
      return;
    }

    if ( this.overlay_element.style.display === "none" || !this.overlay_element.style.opacity )
      this.overlay_element.style.opacity = "0";
    this.overlay_element.style.display = "block";
    fadeBackdrop( this.overlay_element, 1, this.BACKDROP_FADE_MS );
  }

  private static updateViewportBackground(): void
  {
    if ( !this.overlay_element )
      return;

    // An empty inline value restores the regular dimmed backdrop from CSS.
    this.overlay_element.style.background = this.topOverlaySafe()?.getViewportBackground() ?? "";
  }
}
